/**
 * Step 01 — Profile
 *
 * Fields: firstName, middleName, lastName, displayName, gender, dateOfBirth,
 * whatsappNumber, primaryLanguage, secondaryLanguages,
 * familyReligion, personalReligion, reservationStatus, minorityStatus
 *
 * @module udp-schema/step-01
 */

import { z } from 'zod';
import { GENDER_VALUES, RELIGION_VALUES } from './shared-enums.js';
import {
  emptyStringToUndefined,
  optionalPhoneSchema,
  optionalTrimmedString,
} from './zod-helpers.js';

// ─── Main Step 01 Schema ────────────────────────────────────────────────────

export const step01ProfileSchema = z.object({
  firstName: z.string().trim().max(50).optional(),
  middleName: optionalTrimmedString,
  lastName: z.string().trim().max(50).optional(),

  /** Free, user-chosen display name, independent of the full name and never
   *  verification-locked. Shown in preference to the full name everywhere.
   *  Uses `optionalTrimmedString` (max 100) so an empty string is coerced to
   *  `undefined` and can't be persisted as a blank displayName. */
  displayName: optionalTrimmedString,

  /** Gender — canonical 17-value enum from `shared-enums`. Shared with
   *  dating-schema and any future service profile. */
  gender: z.enum(GENDER_VALUES).optional(),

  dateOfBirth: z.preprocess(emptyStringToUndefined, z.coerce.date().optional()),

  whatsappNumber: optionalPhoneSchema,

  /** Single language code string (e.g., "hindi", "english") — matches dating motherTongue options */
  primaryLanguage: z.string().trim().optional(),

  /** Array of language code strings */
  secondaryLanguages: z.array(z.string().trim()).max(10).optional(),

  /** Family religion — canonical 22-value enum from `shared-enums`. */
  familyReligion: z.enum(RELIGION_VALUES).optional(),

  /** Personal religion — same canonical list as `familyReligion`. */
  personalReligion: z.enum(RELIGION_VALUES).optional(),

  /** "Same as family" toggle for personal religion. When true the UI mirrors
   *  `familyReligion` into `personalReligion` and keeps them in sync. The flag
   *  is persisted (like step-11's `sameAsCurrent`) so the checkbox state and
   *  the live-link survive a resume, rather than being lost on reload. */
  sameAsFamily: z.boolean().optional(),

  /** Reservation / caste category — exhaustive per central + state notifications. */
  reservationStatus: z
    .enum([
      'SC',
      'ST',
      'OBC_NCL',
      'OBC_CL',
      'MBC',
      'SEBC',
      'SBC',
      'DNT_NT',
      'EWS',
      'GENERAL',
      'DONT_KNOW',
      'PREFER_NOT_TO_SAY',
    ])
    .optional(),

  /** Minority community status — per Ministry of Minority Affairs (6 religious + linguistic). */
  minorityStatus: z
    .enum([
      'NO',
      'MUSLIM',
      'CHRISTIAN',
      'SIKH',
      'BUDDHIST',
      'JAIN',
      'PARSI',
      'LINGUISTIC',
      'BOTH',
      'NOT_SURE',
      'PREFER_NOT_TO_SAY',
    ])
    .optional(),
});

// ─── Inferred types ─────────────────────────────────────────────────────────

export type Step01ProfileInput = z.infer<typeof step01ProfileSchema>;
